import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import CookieModal from './components/CookieModal';
import { useCookieConsent } from './context/CookieContext';
import { useLanguage } from './context/LanguageProvider';
import { LANDING_REGISTER_URL, landingSectionFlags, renderDisabledSectionScaffolds } from './landing/config';
import { getLandingContent } from './landing/content';
import { LegalModal } from './landing/components/LegalModal';
import { getLandingTranslation } from './landing/i18n';
import { BottomCtaSection } from './landing/sections/BottomCtaSection';
import { CommunityBooksSection } from './landing/sections/CommunityBooksSection';
import { EvolutionGridSection } from './landing/sections/EvolutionGridSection';
import { FaqSection } from './landing/sections/FaqSection';
import { FooterSection } from './landing/sections/FooterSection';
import { HeroSection } from './landing/sections/HeroSection';
import { LanguageWorldSection } from './landing/sections/LanguageWorldSection';
import { ReadyToBeginSection } from './landing/sections/ReadyToBeginSection';
import { StorytellersSection } from './landing/sections/StorytellersSection';
import type { LegalDocKey } from './landing/types';

function Home() {
  const { language } = useLanguage();
  const { consent, isConsentSet } = useCookieConsent();

  const [legalOpen, setLegalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocKey>('terms');
  const [legalHtml, setLegalHtml] = useState('');
  const [legalLoading, setLegalLoading] = useState(false);
  const [legalError, setLegalError] = useState<string | null>(null);

  const t = getLandingTranslation(language);
  const content = useMemo(() => getLandingContent(t), [t]);
  const tracking = useMemo(
    () => ({
      language,
      hasAnalyticsConsent: isConsentSet && consent.analytics,
      registerUrl: LANDING_REGISTER_URL,
    }),
    [consent.analytics, isConsentSet, language]
  );

  const legalDocs = useMemo(
    () =>
      ({
        terms: {
          title: 'Terms of Use',
          url: '/legal/en/terms_of_service_29_11_2025.html',
        },
        privacy: {
          title: 'Privacy & Cookies Policy',
          url: '/legal/en/privacy_and_cookie_policy_29_11_2025.html',
        },
        'user-safety-code': {
          title: 'User Safety Codex',
          url: '/legal/en/user_safety_code_29_11_2025.html',
        },
      }) as const,
    []
  );

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (!legalOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' ||
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'Spacebar'
      ) {
        event.preventDefault();
        setLegalOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [legalOpen]);

  useEffect(() => {
    if (!legalOpen) {
      return;
    }

    const controller = new AbortController();

    const loadDocument = async () => {
      setLegalLoading(true);
      setLegalError(null);

      try {
        const response = await fetch(legalDocs[legalDoc].url, {
          method: 'GET',
          signal: controller.signal,
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();
        setLegalHtml(html);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }

        setLegalHtml('');
        setLegalError('Document could not be loaded. Please try again later.');
      } finally {
        setLegalLoading(false);
      }
    };

    loadDocument();
    return () => controller.abort();
  }, [legalDoc, legalDocs, legalOpen]);

  const openLegal = (doc: LegalDocKey) => {
    setLegalDoc(doc);
    setLegalOpen(true);
  };

  const renderSection = (enabled: boolean, section: ReactNode, key: string) => {
    if (!enabled && !renderDisabledSectionScaffolds) {
      return null;
    }

    return (
      <div
        key={key}
        aria-hidden={!enabled}
        className={!enabled ? 'pointer-events-none opacity-40 grayscale' : undefined}
      >
        {section}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white text-[#1C1C1E]">
      {renderSection(
        landingSectionFlags.hero,
        <HeroSection t={t} tracking={tracking} content={content.hero} />,
        'hero'
      )}
      {renderSection(
        landingSectionFlags.storytellers,
        <StorytellersSection content={content.storytellers} />,
        'storytellers'
      )}
      {renderSection(
        landingSectionFlags.evolutionGrid,
        <EvolutionGridSection t={t} content={content.evolutionGrid} tracking={tracking} />,
        'evolution-grid'
      )}
      {renderSection(
        landingSectionFlags.languageWorld,
        <LanguageWorldSection content={content.languageWorld} />,
        'language-world'
      )}
      {renderSection(
        landingSectionFlags.readyToBegin,
        <ReadyToBeginSection content={content.readyToBegin} tracking={tracking} />,
        'ready-to-begin'
      )}
      {renderSection(
        landingSectionFlags.communityBooks,
        <CommunityBooksSection content={content.communityBooks} tracking={tracking} />,
        'community-books'
      )}
      {renderSection(
        landingSectionFlags.faq,
        <FaqSection content={content.faq} />,
        'faq'
      )}
      {renderSection(
        landingSectionFlags.bottomCta,
        <BottomCtaSection t={t} content={content.bottomCta} tracking={tracking} />,
        'bottom-cta'
      )}
      {renderSection(
        landingSectionFlags.footer,
        <FooterSection t={t} tracking={tracking} openLegal={openLegal} />,
        'footer'
      )}

      <LegalModal
        open={legalOpen}
        legalDoc={legalDoc}
        legalDocs={legalDocs}
        legalHtml={legalHtml}
        legalLoading={legalLoading}
        legalError={legalError}
        onClose={() => setLegalOpen(false)}
      />
      <CookieModal />
    </div>
  );
}

export default Home;
